'use client';

import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

interface TurnstileApi
{
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global
{
  interface Window
  {
    turnstile?: TurnstileApi;
  }
}

interface Props
{
  className?: string;
  /** Whenever this value changes the widget is reset (Turnstile tokens are single-use). */
  resetKey?: unknown;
}

/**
 * Invisible Cloudflare Turnstile widget. Must be rendered inside a <form>; Turnstile injects
 * a hidden `cf-turnstile-response` input into that form. Renders nothing when no site key is configured.
 */
export default function TurnstileWidget({ className = '', resetKey }: Props) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    if (!siteKey || !scriptReady || !containerRef.current || !window.turnstile) return;

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      appearance: 'interaction-only',
      size: 'flexible',
      theme: 'light',
      language: 'cs',
    });

    return () => {
      if (widgetIdRef.current)
      {
        try { window.turnstile?.remove(widgetIdRef.current); } catch { /* widget already gone */ }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, scriptReady]);

  useEffect(() => {
    if (widgetIdRef.current && window.turnstile)
    {
      try { window.turnstile.reset(widgetIdRef.current); } catch { /* ignore */ }
    }
  }, [resetKey]);

  if (!siteKey) return null;

  return (
    <>
      <Script src={ 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit' }
              strategy={ 'afterInteractive' }
              onReady={ () => setScriptReady(true) } />
      <div ref={ containerRef } className={ className } />
    </>
  );
}
